import React, { useEffect, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { head, is, isEmpty, isNil, uniq } from 'ramda';

// store
import { storeGet, useStoreValue } from '../../../../../../store';

// tools
import renderHtmlContent from '../../../../../../helpers/renderHtmlContent';

// components
import ElementSettingRepeater from '../ElementSettingRepeater';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Editor } from 'primereact/editor';
import ElementSettingTableColumns from '../ElementSettingTableColumns';
import { InputSwitch } from 'primereact/inputswitch';
import ElementSettingChips from '../ElementSettingChips';
import ElementSettingCriteriaTableColumns from '../ElementSettingCriteriaTableColumns';
import ElementSettingTableColumnsForCsv from '../ElementSettingTableColumnsForCsv';

import { mimeTypes } from '../../../../../../configData';
import ElementSettingReportHeader from '../ElementSettingReportHeader';
import ElementSettingReportEnable from '../ElementSettingReportEnable';
import getTokens from '../../../../../../helpers/getTokens';


const ElementSetting = ({ setting, changeFn, updateDataFn, bandoStatus }) => {
    const [existingVars, setExistingVars] = useState([]);
    const documentCategories = useStoreValue('documentCategories');

    const settingLabels = {
        label: __('Label', 'htr'),
        placeholder: __('Segnaposto', 'htr'),
        step: __('Numero Decimali', 'htr'),
        isRequestedAmount: __('Importo richiesto', 'htr'),
        isDelegation: __('Delega', 'htr'),
        isPecEmail: __('PEC email', 'htr'),
        options: __('Opzioni', 'htr'),
        mime: __('Tipo di file', 'htr'),
        documentCategories: __('Categorie dei documenti', 'htr'),
        text: __('Testo formattato', 'htr'),
        table_columns: '',
        criteria_table_columns: '',
        variable: __('Variabile (lettere, cifre e "_"; il primo carattere deve essere una lettera!)', 'htr'),
        formula: __('Formula di calcolo automatico', 'htr'),
        isChecklistItem: __('Fa parte di "checklist"?', 'htr'),
        reportEnable: __('Aggiungere nel report CSV?', 'htr'),
        reportHeader: __('Nome della colonna nel CSV', 'htr'),
        reportColumns: __('', 'htr')
    }

    const settingDescription = {
        formula: __('Crea una formula usando variabili dichiarate in precedenza. Utilizza questi operatori matematici: <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>. Esempio: <code>{entrate}+{assicurazione}</code>.', 'htr')
    }

    const renderHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const header = renderHeader();

    const invalidFormula = (value) => {
        try {
            getTokens(value)
            return false;
        } catch {
            return __('Potrebbe essere un errore nella formula!')
        }
    }

    const getProperField = (setting) => {
        if (setting.name === 'options') {
            return <ElementSettingRepeater
                value={is(Array, setting.value) ? setting.value : []}
                name={setting.name}
                bandoStatus={bandoStatus}
                setDataFn={updateDataFn}/>
        } else if (setting.name === 'mime') {
            return <MultiSelect
                value={is(Array, setting.value) ? setting.value : []}
                onChange={(e) => updateDataFn(setting.name, e.value)}
                options={mimeTypes}
                optionLabel="name"
                display="chip"
                placeholder={__('Scegli', 'htr')}/>
        } else if (setting.name === 'documentCategories') {
            return <MultiSelect
                value={is(Array, setting.value) ? setting.value : []}
                onChange={(e) => updateDataFn(setting.name, e.value)}
                options={documentCategories}
                display="chip"
                placeholder={__('Scegli', 'htr')}/>
        } else if (setting.name === 'text') {
            return <div translate="no"><Editor
                value={setting.value}
                headerTemplate={header}
                onTextChange={(e) => changeFn(e.htmlValue, setting.name)}
                style={{ height: 80 * 4 }}
            /></div>
        } else if (setting.name === 'step') {
            return <InputText id={setting.name}
                              keyfilter="int"
                              aria-describedby={`${setting.name}-help`}
                              placeholder="0"
                              value={setting.value}
                              onChange={(e) => changeFn(e.target.value, setting.name)}/>
        } else if (setting.name === 'table_columns') {
            return <ElementSettingTableColumns
                value={is(Object, setting.value) ? setting.value : {}}
                name={setting.name}
                bandoStatus={bandoStatus}
                setDataFn={updateDataFn}/>
        } else if (setting.name === 'criteria_table_columns') {
            return <ElementSettingCriteriaTableColumns
                value={is(Object, setting.value) ? setting.value : {}}
                name={setting.name}
                bandoStatus={bandoStatus}
                setDataFn={updateDataFn}/>
        } else if (['isRequestedAmount', 'isDelegation', 'isPecEmail', 'isChecklistItem'].includes(setting.name)) {
            return <InputSwitch
                checked={setting.value}
                onChange={(e) => changeFn(e.value, setting.name)}/>
        } else if (['reportEnable'].includes(setting.name)) {
            return <ElementSettingReportEnable setting={setting} setDataFn={updateDataFn}/>
        } else if (setting.name === 'reportColumns') {
            return <ElementSettingTableColumnsForCsv
                value={is(Object, setting.value) ? setting.value : {}}
                name={setting.name}
                bandoStatus={bandoStatus}
                setDataFn={updateDataFn}/>
        } else if (setting.name === 'reportHeader') {
            return <ElementSettingReportHeader setting={setting} changeFn={changeFn}/>
        } else if (['variable'].includes(setting.name)) {
            return <ElementSettingChips
                restrictedValues={[]}
                changeFn={(value) => changeFn(value, setting.name)}
                value={setting.value}/>
        } else if (setting.name === 'table_columns') {
            return <ElementSettingTableColumns
                value={is(Object, setting.value) ? setting.value : {}}
                name={setting.name}
                bandoStatus={bandoStatus}
                setDataFn={updateDataFn}/>
        } else if (setting.name === 'formula') {
            const isInvalid = invalidFormula(setting.value);
            return <>
                <InputText id={setting.name} aria-describedby={`${setting.name}-help`}
                              value={setting.value}
                              onChange={(e) => changeFn(e.target.value, setting.name)}/>
                <p style={{margiTop: 0, fontSize: '14px', color: '#df3636'}}>{isInvalid}</p>
                </>
        } else {
            return <InputText id={setting.name} aria-describedby={`${setting.name}-help`}
                              value={setting.value}
                              onChange={(e) => changeFn(e.target.value, setting.name)}/>
        }
    }

    useEffect(() => {
        const elements = storeGet('formElements');
        const activeElement = storeGet('activeElement');
        const vars = elements
            .filter(o => o.id !== activeElement)
            // eslint-disable-next-line
            .map((o) => {
                const variableSetting = head(o.settings.filter(s => s.name === 'variable'));
                if (variableSetting) {
                    return variableSetting.value[0];
                }
            })
            .filter(v => !isNil(v));

        setExistingVars(uniq(vars));
    }, []);

    return <div className="formElementSettings__field" key={setting.name}>
        <label htmlFor={setting.name}>{settingLabels[setting.name]}</label>
        {getProperField(setting)}
        {setting.name === 'formula' && !isEmpty(existingVars)
            ? <div className="formElementSettings__fieldVarsList">
                <p>Existing variables: {existingVars.map(v => <code key={v}>{`{${v}}`}</code>)}</p>
            </div> : null}
        {settingDescription[setting.name]
            ? <div className="formElementSettings__fieldDescription">
                <p>{renderHtmlContent(settingDescription[setting.name])}</p>
            </div> : null}
    </div>
}

export default ElementSetting;